/**
 * Mission Control's route table, in one place so App.tsx and the contract tests
 * read the SAME routes. An ARRAY of `<Route>` elements rather than inline JSX
 * children, because a test that had to find them in App.tsx's source text needed
 * a hand-written JSX parser to do it, and each round of review found another form
 * that parser read wrongly — a route it skipped is a route nothing measures.
 * Exported as values, `props.path` is what react-router itself reads.
 *
 * ORDER is this array's order, and `*` stays last. react-router@7 ranks patterns
 * rather than taking the first match, so nothing here depends on position today;
 * keeping the order anyway means a future route with a genuine tie behaves the way
 * it reads.
 *
 * The Fleet Editor's own table is spread in at the position it occupied as a JSX
 * child, so this one array is the whole table App.tsx renders.
 */
import { Navigate, Route } from 'react-router-dom'
import { FleetHealth } from './pages/FleetHealth'
import { Activity } from './pages/Activity'
import { Approvals } from './pages/Approvals'
import { Improvements } from './pages/Improvements'
import { ImprovementPRReview } from './pages/ImprovementPRReview'
import { Investigations } from './pages/Investigations'
import { InvestigationDetail } from './pages/InvestigationDetail'
import { Feedback } from './pages/Feedback'
import { Logs } from './pages/Logs'
import { Diagnostics } from './pages/Diagnostics'
import { LlmUsage } from './pages/LlmUsage'
import { InFlight } from './pages/InFlight'
import { Chat } from './pages/Chat'
import { Help } from './pages/Help'
import { ModulePage } from './pages/ModulePage'
import { PluginPage } from './pages/PluginPage'
import { FileLinkPage } from './pages/FileLinkPage'
import { Updates } from './pages/Updates'
import { fleetEditorRoutes } from './pages/fleetEditorRoutes'

export const appRoutes = [
  <Route key="health" path="/" element={<FleetHealth />} />,
  <Route key="activity" path="/activity" element={<Activity />} />,
  <Route key="approvals" path="/approvals" element={<Approvals />} />,
  <Route key="improvements" path="/improvements" element={<Improvements />} />,
  <Route key="improvement-pr" path="/improvements/:id/pr" element={<ImprovementPRReview />} />,
  <Route key="investigations" path="/investigations" element={<Investigations />} />,
  <Route key="investigation" path="/investigations/:id" element={<InvestigationDetail />} />,
  <Route key="feedback" path="/feedback" element={<Feedback />} />,
  <Route key="logs" path="/logs" element={<Logs />} />,
  <Route key="diagnostics" path="/diagnostics" element={<Diagnostics />} />,
  <Route key="llm-usage" path="/llm-usage" element={<LlmUsage />} />,
  <Route key="llm-usage-traces" path="/llm-usage/traces" element={<LlmUsage />} />,
  <Route key="llm-usage-trace" path="/llm-usage/trace" element={<LlmUsage />} />,
  <Route key="in-flight" path="/in-flight" element={<InFlight />} />,
  <Route key="chat" path="/chat" element={<Chat />} />,
  // ADR-111 D1/IA-6: the section is the Fleet Editor at /fleet-editor,
  // agent-centric per the 2026-07-28 IA amendment. Its routes live in
  // pages/fleetEditorRoutes.tsx so the shell's contract tests render the
  // same table this app does. /workflows is kept as a permanent redirect.
  ...fleetEditorRoutes,
  <Route key="workflows" path="/workflows" element={<Navigate to="/fleet-editor" replace />} />,
  <Route key="workflows-splat" path="/workflows/*" element={<Navigate to="/fleet-editor" replace />} />,
  // ADR-099 D8 — the "Core Updates" operator surface.
  <Route key="updates" path="/updates" element={<Updates />} />,
  <Route key="help" path="/help" element={<Help />} />,
  <Route key="module" path="/modules/:id" element={<ModulePage />} />,
  <Route key="plugin" path="/plugins/:id" element={<PluginPage />} />,
  // ADR-210 — the permanent signed-in link to a project artifact: /files/<scope>/<path>.
  <Route key="file-link" path="/files/:scope/*" element={<FileLinkPage />} />,
  // Fallback: an unknown path RENDERS health in place, leaving the URL alone —
  // not a redirect. utils/pageLabel names the page from this component, so the
  // two must change together.
  <Route key="catch-all" path="*" element={<FleetHealth />} />,
]
